import {
	AssignedToText,
	CompletedRequestsText,
	ContactCareForSearchHelpText,
	CustomItemNameText,
	CustomQuoteNumberText,
	CustomQuoteRequestACheckYourSpellingBullet,
	CustomQuoteRequestAdjustYourFiltersBullet,
	CustomQuoteRequestNoResultsFoundSubheader,
	CustomQuoteRequestsNoResultsFoundHeader,
	CustomQuoteRequestSubmittingBullet,
	DesignerNumberText,
	LastQuotedByText,
	LastUpdatedText,
	NewRequestsText,
	OpenText,
	ProductLineText,
	RequestSearchInputText,
	UnavailableDataPlaceholderText
} from "constants/text.ts";
import SortableTableHead, { HeadCell } from "../Common/Table/SortableTableHead.tsx";
import { Fragment, SyntheticEvent, useMemo, useState } from "react";
import { StyledTableCell, StyledTableWrapper } from "../Common/Table/StyledTable.styles.ts";
import { format } from "date-fns";
import { LinkButton } from "../Common/Link";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import { ViewQuoteTableContainer } from "./ViewQuoteTable/ViewQuoteTable.styles.ts";
import { Tab, TableBody, TableRow, Tabs } from "@mui/material";
import useSession from "hooks/useSession.ts";
import { FilterOpts } from "pages/OrderPages/OrderListPage/OrderListPage.tsx";
import { InitialFilterOptions, RequestCacheKeys } from "pages/CustomQuote/customQuoteConstants.ts";
import CustomQuoteRequestTableFilter from "./CustomQuoteRequestTableFilter.tsx";
import SuggestedSearch from "../Common/SuggestedSearch/SuggestedSearch.tsx";
import {
	CustomQuoteRequestNoResultsFoundContainer,
	CustomQuoteRequestTableHeader,
	FilteredRequestsTableWrapper
} from "./CustomQuoteRequestTable.styles.ts";
import { models } from "../../types/api/viewModels.ts";
import {
	getAssigneeName,
	getProductLine,
	getRequestNames,
	getRevisionIdNumbers,
	isCompletedRequestStatus
} from "./customQuoteUtils.ts";
import { displayDesigner } from "../../utils/order.ts";
import TableSkeletonWithTabs from "../Common/Table/TableSkeleton/TableSkeletonWithTabs.tsx";
import { useNavigate } from "react-router";

interface CustomQuoteRequestTableProps {
	customQuotes: models["CustomQuoteViewModel"][];
	isLoading?: boolean;
}

const CustomQuoteRequestTable = ({ customQuotes, isLoading }: CustomQuoteRequestTableProps) => {
	const baseNewRequestTableHeadCells = useMemo(
		() =>
			[
				{ id: "updatedAt", label: LastUpdatedText, sortable: true },
				{ id: "customQuoteNumber", label: CustomQuoteNumberText },
				{ id: "customItemName", label: CustomItemNameText },
				{ id: "productLine", label: ProductLineText },
				{ id: "DesignerNumber", label: DesignerNumberText },
				{ id: "LastQuotedBy", label: LastQuotedByText },
				{ id: "assignedTo", label: AssignedToText },
				{ id: "open", label: "" }
			].map((cell) => ({
				...cell,
				disablePadding: false,
				numeric: false,
				sortable: cell.sortable ?? false
			})) satisfies HeadCell[],
		[]
	);

	const [completedRequestFilterOptions, setCompletedRequestFilterOptions] = useSession<FilterOpts>(
		RequestCacheKeys.RequestFilters,
		InitialFilterOptions
	);

	const [newRequestFilterOptions, setNewRequestFilerOptions] = useSession<FilterOpts>(
		RequestCacheKeys.RequestFilters,
		InitialFilterOptions
	);

	const completedRequests = customQuotes.filter((request: models["CustomQuoteViewModel"]) =>
		isCompletedRequestStatus(request)
	);
	const newRequests = customQuotes.filter(
		(request: models["CustomQuoteViewModel"]) => !isCompletedRequestStatus(request)
	);

	const [searchValue, setSearchValue] = useState<string | null>(null);
	const navigate = useNavigate();

	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort("updatedAt", "asc");
	const sortedNewRequests = newRequests.slice().sort(getComparator(order, orderBy));
	const sortedCompletedRequests = completedRequests.slice().sort(getComparator(order, orderBy));
	const [isCompletedRequest, setIsCompletedRequest] = useState(false);

	const [tabValue, setTabValue] = useState(0);
	const handleTabChange = (event: SyntheticEvent, selectedTabIndex: number) => {
		setTabValue(selectedTabIndex);
		setIsCompletedRequest(selectedTabIndex === 1);
	};

	const displayedRequests = tabValue === 0 ? sortedNewRequests : sortedCompletedRequests;
	const filteredRequests = useMemo(() => {
		const filterOptions = isCompletedRequest ? completedRequestFilterOptions : newRequestFilterOptions;
		let filtered = displayedRequests;

		if (filterOptions?.quotedBy && filterOptions?.quotedBy.length > 0) {
			filtered = filtered.filter((request) => filterOptions?.quotedBy?.includes(getAssigneeName(request)));
		}

		if (filterOptions?.productLines && filterOptions?.productLines.length > 0) {
			filtered = filtered.filter((request) => {
				const productLine = getProductLine(request);
				return !!productLine && filterOptions?.productLines?.includes(productLine);
			});
		}

		if (searchValue && searchValue.trim() !== "") {
			const search = searchValue.trim().toLowerCase();
			filtered = filtered.filter(
				(request) =>
					getAssigneeName(request)?.toLowerCase().includes(search) ||
					request?.revisions
						?.flatMap((revision) => revision.revisionId)
						.toString()
						.includes(searchValue) ||
					request?.requestName?.toLowerCase().includes(search)
			);
		}

		return filtered;
	}, [displayedRequests, completedRequestFilterOptions, newRequestFilterOptions, isCompletedRequest, searchValue]);

	const filteredHeadCells = useMemo(
		() => baseNewRequestTableHeadCells.filter((cell) => !(tabValue !== 0 && cell.id === "assignedTo")),
		[tabValue, baseNewRequestTableHeadCells]
	);

	const searchOptions = useMemo(() => {
		const requestNames = getRequestNames(customQuotes) ?? [];
		const revisionIds = getRevisionIdNumbers(customQuotes) ?? [];

		return Array.from(new Set([...requestNames, ...revisionIds]));
	}, [customQuotes]);

	const handleDraftSearch = (_event: SyntheticEvent, values: string | null) => {
		setSearchValue(values ?? null);
	};

	const GetNewRequestTableRows = (request: models["CustomQuoteViewModel"]) => {
		const requestTableCells = [
			{
				testId: "request-table-lastUpdated",
				props: { fixedWidth: "140px" },
				content: request.updatedAt
					? format(request.updatedAt, "MM/dd/yy hh:mm:ss")
					: UnavailableDataPlaceholderText
			},
			{
				testId: "request-table-customQuoteNumber",
				props: { fixedWidth: "80px" },
				content: request?.revisions?.length
					? [...request.revisions]
							.sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime())
							.map((revision) => <div key={revision.revisionId}>{revision.revisionId}</div>)
					: UnavailableDataPlaceholderText
			},
			{
				testId: "request-table-customItemName",
				props: { minWidth: "125px", maxWidth: "170.5px" },
				content: request?.requestName ?? UnavailableDataPlaceholderText
			},
			{
				testId: "request-table-productLine",
				props: { minWidth: "100px", maxWidth: "170.5px" },
				content: getProductLine(request) ?? UnavailableDataPlaceholderText
			},
			{
				testId: "request-table-designerNumber",
				props: { minWidth: "100px", maxWidth: "170.5px" },
				content:
					displayDesigner(request.designer ?? request.designerId) === ""
						? UnavailableDataPlaceholderText
						: displayDesigner(request.designer ?? request.designerId)
			},
			{
				testId: "request-table-lastQuotedBy",
				props: { minWidth: "100px", maxWidth: "110px" },
				content: getAssigneeName(request) ?? UnavailableDataPlaceholderText
			}
		];

		return (
			<Fragment>
				{requestTableCells.map(({ testId, props, content }) => (
					<StyledTableCell
						key={testId}
						data-testid={testId}
						{...props}
					>
						{content}
					</StyledTableCell>
				))}
				{tabValue === 0 && (
					<StyledTableCell
						data-testid="request-table-assignedTo"
						minWidth="100px"
						maxWidth="110px"
					>
						{getAssigneeName(request) ?? UnavailableDataPlaceholderText}
					</StyledTableCell>
				)}
				<StyledTableCell
					fixedWidth="40px"
					isLastIndex
				>
					<LinkButton
						onClick={() => navigate(`/custom-quote/${request.quoteId}`)}
						data-testid="quotes-table-open-link"
					>
						{OpenText}
					</LinkButton>
				</StyledTableCell>
			</Fragment>
		);
	};

	return (
		<ViewQuoteTableContainer
			elevation={1}
			square
		>
			{isLoading ? (
				<TableSkeletonWithTabs tabLabels={[NewRequestsText, CompletedRequestsText]} />
			) : (
				<>
					<CustomQuoteRequestTableHeader>
						<Tabs
							value={tabValue}
							onChange={handleTabChange}
							textColor="primary"
						>
							<Tab
								label={NewRequestsText}
								data-testid="new-requests-tab"
							/>
							<Tab
								label={CompletedRequestsText}
								data-testid="completed-requests-tab"
							/>
						</Tabs>
						{customQuotes && !isCompletedRequest && (
							<CustomQuoteRequestTableFilter
								activeView="New"
								filterOptions={newRequestFilterOptions}
								setFilterOptions={setNewRequestFilerOptions}
								requests={customQuotes}
							/>
						)}

						{customQuotes && isCompletedRequest && (
							<CustomQuoteRequestTableFilter
								activeView="Completed"
								filterOptions={completedRequestFilterOptions}
								setFilterOptions={setCompletedRequestFilterOptions}
								requests={customQuotes}
							/>
						)}
						<SuggestedSearch
							ariaLabel="search-requests"
							dataTestId="custom-quote-request-table-search"
							dropDownOpenOnChar={3}
							placeholder={RequestSearchInputText}
							handleSearch={(event, values) => handleDraftSearch(event, values ?? "")}
							id="custom-quote-request-table-search-requests"
							searchOptions={searchOptions ?? []}
							searchValue={searchValue ?? ""}
							size="small"
						/>
					</CustomQuoteRequestTableHeader>
					{filteredRequests.length > 0 ? (
						<FilteredRequestsTableWrapper>
							<StyledTableWrapper
								data-testid="new-requests-table"
								aria-label="New Requests Table"
							>
								<SortableTableHead
									orderBy={orderBy}
									order={order}
									onRequestSort={handleRequestSort}
									headCells={filteredHeadCells}
								/>
								<TableBody>
									{filteredRequests.map((request: models["CustomQuoteViewModel"]) => (
										<TableRow
											key={request.quoteId}
											data-testid={`request-${request.quoteId}`}
										>
											{GetNewRequestTableRows(request)}
										</TableRow>
									))}
								</TableBody>
							</StyledTableWrapper>
						</FilteredRequestsTableWrapper>
					) : (
						<CustomQuoteRequestNoResultsFoundContainer>
							<img
								src="/assets/CustomQuoteNoResults.svg"
								alt="No Results Found"
								data-testid="custom-quote-no-results-found"
							/>
							<h1>{CustomQuoteRequestsNoResultsFoundHeader}</h1>
							<div>
								<div>{CustomQuoteRequestNoResultsFoundSubheader}</div>
								<ul>
									<li>{CustomQuoteRequestAdjustYourFiltersBullet}</li>
									<li>{CustomQuoteRequestACheckYourSpellingBullet}</li>
									<li>{CustomQuoteRequestSubmittingBullet}</li>
								</ul>
							</div>
							<div>{ContactCareForSearchHelpText}</div>
						</CustomQuoteRequestNoResultsFoundContainer>
					)}
				</>
			)}
		</ViewQuoteTableContainer>
	);
};

export default CustomQuoteRequestTable;
